import React, { useRef, useState } from 'react';
import ReactPlayer from 'react-player';

// Component responsabile della riproduzione video con enforcement:
// - di default i controlli sono nascosti per evitare salti nel contenuto
// - se l'utente ha superato il quiz con almeno l'80%, i controlli vengono abilitati
export default function VideoEnforced({ url, onCompleted, onProgress, progressReady = true, progressError, canControl = false }) {
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [maxWatched, setMaxWatched] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [completionError, setCompletionError] = useState(false);
  const completionRequestedRef = useRef(false);
  const lastCompletionSecondsRef = useRef(0);

  const formatTime = (sec) => {
    const t = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const requestCompletion = async (seconds) => {
    if (completed || completionRequestedRef.current) return;

    completionRequestedRef.current = true;
    lastCompletionSecondsRef.current = seconds;
    setCompletionError(false);

    try {
      if (onCompleted) await onCompleted(seconds);
      setCompleted(true);
    } catch (error) {
      completionRequestedRef.current = false;
      setCompletionError(true);
      console.error('Salvataggio completamento video fallito', error);
    }
  };

  const handleProgress = (state) => {
    const s = state.playedSeconds || 0;
    setPlayedSeconds(s);
    if (s > maxWatched) setMaxWatched(s);
    if (onProgress) onProgress(s);
    if (!completed && !completionRequestedRef.current && duration > 0 && s >= duration - 0.5) {
      requestCompletion(s);
    }
  };

  const handleSeek = (seconds) => {
    // Se i controlli sono abilitati (quiz superato >=80%), consenti la navigazione libera
    // Altrimenti, blocca i salti oltre la porzione già vista (enforcement)
    if (canControl) return;
    if (seconds > maxWatched + 1 && playerRef.current) {
      playerRef.current.seekTo(maxWatched, 'seconds');
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:h-full tw:overflow-hidden" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="tw:flex-1 tw:bg-black tw:relative tw:flex tw:items-center tw:justify-center tw:min-h-0" style={{ flex: '1 1 0%', backgroundColor: 'black', position: 'relative', minHeight: 0 }}>
        <ReactPlayer
          ref={playerRef}
          url={url}
          width="100%"
          height="100%"
          className="tw:absolute tw:top-0 tw:left-0"
          style={{ objectFit: 'contain' }}
          playing={playing}
          // Abilita i controlli solo se l'utente ha superato il quiz (>=80%)
          controls={canControl}
          onProgress={handleProgress}
          onDuration={setDuration}
          onSeek={handleSeek}
          onEnded={() => {
            requestCompletion(duration);
          }}
        />
      </div>

      <div className="tw:flex tw:items-center tw:gap-3">
        <button
          type="button"
          className="tw:px-4 tw:py-2 tw:bg-blue-600 tw:text-white tw:rounded"
          onClick={() => setPlaying((p) => !p)}
          disabled={!progressReady}
        >
          {!progressReady ? 'Preparazione...' : (playing ? 'Pausa' : 'Riproduci')}
        </button>

        <div className="tw:flex-1 tw:h-2 tw:bg-gray-200 tw:rounded">
          <div
            className="tw:h-2 tw:bg-blue-500 tw:rounded"
            style={{ width: duration ? `${(playedSeconds / duration) * 100}%` : '0%' }}
          />
        </div>

        <span className="tw:text-sm tw:text-gray-600">
          {formatTime(playedSeconds)} / {formatTime(duration)}
        </span>
      </div>

      {/* Messaggistica di stato per chiarezza utente */}
      {!canControl && (
        <div className="tw:p-3 tw:bg-yellow-50 tw:text-yellow-800 tw:rounded">
          I controlli del player sono disabilitati per evitare salti nel video.
           Supera il quiz con almeno l'80% di risposte corrette per sbloccarli.
        </div>
      )}
      {!progressReady && (
        <div className="tw:p-3 tw:bg-yellow-50 tw:text-yellow-800 tw:rounded">
          Preparazione della sessione di riproduzione in corso.
          {progressError ? ` ${progressError}` : ''}
        </div>
      )}
      {progressReady && progressError && (
        <div className="tw:p-3 tw:bg-yellow-50 tw:text-yellow-800 tw:rounded">
          Sincronizzazione della riproduzione temporaneamente non riuscita. Continua a riprodurre il video.
        </div>
      )}
      {canControl && (
        <div className="tw:p-3 tw:bg-green-50 tw:text-green-800 tw:rounded tw:text-center">
           Hai superato il quiz con almeno l'80% di risposte corrette. I controlli del player sono abilitati.
        </div>
      )}

      {completionError && (
        <div className="tw:flex tw:items-center tw:gap-3 tw:p-3 tw:bg-red-50 tw:text-red-700 tw:rounded">
          <span>Salvataggio del completamento fallito.</span>
          <button
            type="button"
            className="tw:px-3 tw:py-1 tw:bg-red-600 tw:text-white tw:rounded"
            onClick={() => requestCompletion(lastCompletionSecondsRef.current)}
          >
            Riprova
          </button>
        </div>
      )}

      {/* {completed && (
        <div className="tw:p-3 tw:bg-green-100 tw:text-green-800 tw:rounded">
          Video completato. Puoi procedere al quiz.
        </div>
      )} */}
    </div>
  );
}
